class QuizDecisionEngine {
  constructor(
    quizResponses = {},
    decisionMap = {},
    storageKey = {
      response: "quiz_response",
      recommendations: "quiz_recommendations",
    }
  ) {
    this.quizResponses = quizResponses;
    this.storageKey = storageKey;
    // Add default values to prevent undefined errors.
    this.decisionMap = Object.assign(
      {
        recommended_page_url: { compute: [], default: "" },
        query_param: { compute: [], default: "" },
        texts: [],
        recommended_products: [],
        recommended_offers: [],
      },
      decisionMap
    );
    this.urlSearchParm = new URLSearchParams(
      this.decisionMap.query_param.default
    );
  }

  // Handle multi-choice and single choice responses
  _compareResponse(quizAnswer, value) {
    const isArray = Array.isArray(quizAnswer);
    if (isArray) {
      return quizAnswer.includes(value);
    } else if (typeof value === "object") {
      return quizAnswer > value.min && quizAnswer <= value.max;
    }
    return quizAnswer === value;
  }

  _isConditionSatisfied(rule, conditions = []) {
    if (rule === "OR") {
      return conditions.some((item) => {
        const key = Object.keys(item)[0];
        const value = item[key];
        const quizAnswer = this.quizResponses[key] || "";
        return (
          this._compareResponse(quizAnswer, value) ||
          (value === "_any_" && quizAnswer.length !== 0)
        );
      });
    } else if (rule === "AND") {
      return conditions.every((item) => {
        const key = Object.keys(item)[0];
        const value = item[key];
        const quizAnswer = this.quizResponses[key];
        return (
          this._compareResponse(quizAnswer, value) ||
          (value === "_any_" && quizAnswer.length !== 0)
        );
      });
    }
    return false;
  }
  _isEmptyObject(data = {}) {
    return Object.keys(data).length === 0;
  }

  getTexts() {
    const textMap = this.decisionMap.texts;
    const texts = {};
    textMap.forEach((textItem) => {
      texts[textItem.default.key] = textItem.default.value;
      textItem.compute.forEach((item) => {
        if (this._isConditionSatisfied(item.rule, item.conditions)) {
          texts[item.key] = item.value || this.quizResponses[item.reference];
        }
      });
    });
    return this._isEmptyObject(texts) ? undefined : texts;
  }

  getRecommendedProducts() {
    const productsMap =
      featureFlag("QUIZ_RECOMMENDED_PRODUCTS", null) ||
      this.decisionMap.recommended_products;

    const productsData = {};

    productsMap.forEach((productItem) => {
      productsData[productItem.default.key] = {
        handle: productItem.default.handle,
        sku: productItem.default.sku,
      };

      productItem.compute.forEach(
        ({ rule, conditions, key, handle, sku, type }) => {
          if (this._isConditionSatisfied(rule, conditions)) {
            productsData[key] = {
              handle: handle,
              ...(sku && { sku }),
              ...(type && { type }),
            };
          }
        }
      );
    });
    return this._isEmptyObject(productsData) ? undefined : productsData;
  }

  getRecommendedOffers() {
    const offersMap = this.decisionMap.recommended_offers;
    const offersData = {};

    offersMap.forEach((offerItem) => {
      offersData[offerItem.default.key] = offerItem.default.value;
      offerItem.compute.forEach((item) => {
        if (this._isConditionSatisfied(item.rule, item.conditions)) {
          offersData[item.key] = item.value;
        }
      });
    });

    // Flatten Object
    const flattenedData = Object.keys(offersData).reduce(
      (prev, curr) => Object.assign({}, prev, offersData[curr]),
      {}
    );
    // Using undefined so the object key won't be attached to the final payload
    return this._isEmptyObject(flattenedData) ? undefined : flattenedData;
  }

  // We would use the response through liquid in the FT landing page
  async persistQuizResponseInCart() {
    const payload = {
      attributes: {
        [this.storageKey.response]: this.quizResponses,
        [this.storageKey.recommendations]: {
          texts: this.getTexts(),
          products: this.getRecommendedProducts(),
          offers: this.getRecommendedOffers(),
        },
      },
    };

    try {
      return fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
          "Content-Type": "application/json",
        },
      });
    } catch (error) {
      logger.error(`Quiz Error: ${error}`);
    }
  }

  getQueryParam() {
    const queryMap = this.decisionMap.query_param;
    queryMap.compute = queryMap.compute || [];

    queryMap.compute.forEach((item) => {
      if (this._isConditionSatisfied(item.rule, item.conditions)) {
        this.urlSearchParm.set(item.key, item.value);
      }
    });

    const queryString = this.urlSearchParm.toString();

    return queryString ? `?${queryString}` : "";
  }

  getRecommendedPage() {
    const pageDecisionMap = this.decisionMap.recommended_page_url;
    pageDecisionMap.compute = pageDecisionMap.compute || [];

    let recommendedPage = pageDecisionMap.default;

    pageDecisionMap.compute.forEach((item) => {
      if (this._isConditionSatisfied(item.rule, item.conditions)) {
        recommendedPage = item.value;
      }
    });

    return `${
      window.Shopify.routes.root
    }${recommendedPage}${this.getQueryParam()}`;
  }
}
